import React, { useEffect, useState, useCallback } from 'react';

import { updateClient, viewClient } from '../../../services/ClientService';

import { BackButton } from '../../common/button/back-button/BackButton';
import { ClientsCreateGeneralTab } from '../clients-create/general/ClientsCreateGeneralTab';
import { ClientsCreateConsentTab } from '../clients-create/consent/ClientsCreateConsentTab';
import { ClientLocatorInformationTab } from '../clients-create/locator-information/ClientLocatorInformationTab';
import { ClientsViewStudy } from './study/ClientsViewStudy';
import { ClientsViewActivityLog } from './activity-log/ClientsViewActivityLog';

import './ClientsView.css';

export const ClientsView = ({
  context,
  setContext,
  navigateTo,
  navigateToWith,
  showToaster,
  setSpinnerText,
  toolTipsEnabled,
  navigateBack,
}) => {
  const [activeTab, setActiveTab] = useState('clientsCreateGeneral');
  const [client, setClient] = useState({});
  const [consent, setConsent] = useState([]);
  const [currentScreen, setCurrentScreen] = useState('clientsCreateGeneral');

  const retrieveClient = useCallback(async (username, externalReference) => {
    setSpinnerText('Retrieving Client');
    const [data, error] = await viewClient(username, externalReference);
    if (data) {
      if (data.responseStatus === 'Success') {
        setClient(data.clientVO);
        setContext((prevState) => ({
          ...prevState,
          activeTab: '',
          viewMode: false,
          breadcrumbs: {
            ...prevState.breadcrumbs,
            viewClientBreadcrumb: {
              ...prevState.breadcrumbs.viewClientBreadcrumb,
              type: 'General',
              screenKey: 'clientsView',
              data: data,
            },
          },
          editMode: true,
        }));
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Client Information Retrieved',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = useCallback(async () => {
    if (context.activeTab === 'clientLocatorInformation') {
      setActiveTab('clientLocatorInformation');
      setCurrentScreen('clientLocatorInformation');

      await retrieveClient(context.username, context.additionalContext);
    } else {
      if (context.additionalContext) {
        if (context.additionalContext.clientVO) {
          setClient(context.additionalContext.clientVO);
        } else {
          await retrieveClient(context.username, context.additionalContext);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retrieveClient]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdate = async (clientToUpdate) => {
    const [data, error] = await updateClient(context.username, clientToUpdate);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Client Updated',
        });
        await retrieveClient(context.username, data.clientExternalReference);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }

    setSpinnerText(null);
  };

  const screenMap = new Map();
  screenMap.set(
    'clientsCreateGeneral',
    <ClientsCreateGeneralTab
      context={context}
      client={client}
      setClient={setClient}
      setContext={setContext}
      navigateTo={navigateTo}
      setCurrentScreen={setCurrentScreen}
      handleUpdate={handleUpdate}
      navigateBack={navigateBack}
      toolTipsEnabled={toolTipsEnabled}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      editMode={true}
    />
  );
  screenMap.set(
    'clientsViewStudy',
    <ClientsViewStudy
      context={context}
      setContext={setContext}
      client={client}
      setClient={setClient}
      navigateTo={navigateTo}
      navigateToWith={navigateToWith}
      navigateBack={navigateBack}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );
  screenMap.set(
    'clientsCreateConsent',
    <ClientsCreateConsentTab
      context={context}
      client={client}
      setClient={setClient}
      consent={consent}
      setConsent={setConsent}
      handleUpdate={handleUpdate}
      setContext={setContext}
      navigateTo={navigateTo}
      setCurrentScreen={setCurrentScreen}
      setActiveTab={setActiveTab}
      toolTipsEnabled={toolTipsEnabled}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      editMode={true}
    />
  );
  screenMap.set(
    'clientLocatorInformation',
    <ClientLocatorInformationTab
      context={context}
      setContext={setContext}
      client={client}
      setClient={setClient}
      navigateTo={navigateTo}
      currentScreen={currentScreen}
      setCurrentScreen={setCurrentScreen}
      toolTipsEnabled={toolTipsEnabled}
      setSpinnerText={setSpinnerText}
      handleUpdate={handleUpdate}
      showToaster={showToaster}
      setActiveTab={setActiveTab}
      editMode={true}
    />
  );
  screenMap.set(
    'clientsViewActivityLog',
    <ClientsViewActivityLog
      context={context}
      client={client}
      setClient={setClient}
      setContext={setContext}
      navigateTo={navigateTo}
      setCurrentScreen={setCurrentScreen}
      navigateBack={navigateBack}
      toolTipsEnabled={toolTipsEnabled}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      setActiveTab={setActiveTab}
    />
  );

  const handleBack = () => {
    navigateBack();
  };

  const handleNavigationClick = (tab) => {
    setActiveTab(tab);
    setCurrentScreen(tab);
  };

  return (
    <div className="clients-create-container">
      <div className="clients-create-header-container">
        <BackButton onClick={handleBack} />
        <h2>Edit/View Client</h2>
      </div>

      <div className="wizard-content-container">
        <div className="wizard-main-content-container">
          <div className="clients-create-container">
            <div className="clients-create-tab-container">
              <div
                className={'clients-create-tab general ' + (activeTab === 'clientsCreateGeneral' ? 'active-tab' : null)}
                onClick={() => handleNavigationClick('clientsCreateGeneral')}
              >
                General
              </div>
              <div
                className={'clients-create-tab consent ' + (activeTab === 'clientsCreateConsent' ? 'active-tab' : null)}
                onClick={() => handleNavigationClick('clientsCreateConsent')}
              >
                Consent
              </div>
              <div
                className={'clients-create-tab study ' + (activeTab === 'clientsViewStudy' ? 'active-tab' : null)}
                onClick={() => handleNavigationClick('clientsViewStudy')}
              >
                Studies
              </div>
              <div
                className={
                  'clients-create-tab locator ' + (activeTab === 'clientLocatorInformation' ? 'active-tab' : null)
                }
                onClick={() => handleNavigationClick('clientLocatorInformation')}
              >
                Locator Information
              </div>
              <div
                className={
                  'clients-create-tab activity-log ' + (activeTab === 'clientsViewActivityLog' ? 'active-tab' : null)
                }
                onClick={() => handleNavigationClick('clientsViewActivityLog')}
              >
                Activity Log
              </div>
            </div>
            <div className={'clients-create-tab-content ' + activeTab}>{screenMap.get(currentScreen)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
